import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import i18n from "../lib/i18n";
import { useViewTransitionNavigate } from "../hooks/useViewTransitionNavigate";
import { useLogout } from "../hooks/useLogout";
import { KeyRound, Settings, User } from "lucide-react";
import { useAuthStore } from "../stores/authStore";
import { useSidebarGroupsStore } from "../stores/sidebarGroupsStore";
import { AppShell, type NavGroup } from "./AppShell";
import { DASHBOARD_ROUTE } from "../lib/constants/authRoutes";
import {
  SETTINGS_ROUTES,
  PROFILE_ROUTE,
  INVOICE_ROUTES,
} from "../lib/constants/settingsRoutes";
import { usePermissions } from "../hooks/usePermissions";
import { PERMISSION_KEYS } from "../lib/constants/permissionKeys";
import { MARKETPLACE_PROVIDERS } from "../types/marketplace";

const TenantLayout: React.FC = () => {
  const navigateRaw = useNavigate();
  const navigate = useViewTransitionNavigate();
  const { pathname } = useLocation();
  const user = useAuthStore((s) => s.user);
  const tenantId = user?.tenantId ?? undefined;
  const { has: hasPermission } = usePermissions();

  const canViewOrders = hasPermission(PERMISSION_KEYS.OrdersRead);
  const canViewInvoices = hasPermission(PERMISSION_KEYS.InvoicesRead);
  const canViewQuotes = hasPermission(PERMISSION_KEYS.QuotesRead);
  const canViewDeliveryNotes = hasPermission(PERMISSION_KEYS.DeliveryNotesRead);
  const canViewProducts = hasPermission(PERMISSION_KEYS.ProductsRead);
  const canViewCategories = hasPermission(PERMISSION_KEYS.CategoriesRead);
  const canViewBrands = hasPermission(PERMISSION_KEYS.BrandsRead);
  const canViewAttributes = hasPermission(PERMISSION_KEYS.AttributesRead);
  const canViewWarehouses = hasPermission(PERMISSION_KEYS.WarehouseRead);
  const canViewStockReceipts = hasPermission(PERMISSION_KEYS.StockReceiptsRead);
  const canViewReports = hasPermission(PERMISSION_KEYS.ReportsRead);
  const canViewCurrentAccounts = hasPermission(PERMISSION_KEYS.CurrentAccountsRead);
  const canViewCashBank = hasPermission(PERMISSION_KEYS.CashBankRead);
  const canViewCheckNotes = hasPermission(PERMISSION_KEYS.CheckNotesRead);
  const canViewMarketplace = hasPermission(PERMISSION_KEYS.MarketplaceRead);
  // İçe Aktarım ekranı en az bir yazma yetkisi (Products veya CurrentAccounts) gerektirir —
  // salt Read ile açılsa da hiçbir sekme gösterilmeyecek boş bir sayfa olurdu.
  const canImportProducts = hasPermission(PERMISSION_KEYS.ProductsWrite);
  const canImportCurrentAccounts = hasPermission(PERMISSION_KEYS.CurrentAccountsWrite);

  // ── i18n language sync ───────────────────────────────────────────
  // i18n.changeLanguage is an external-system call — useEffect is correct here.
  //
  // Precedence rule (intentional): the user's saved account language
  // (localizationSettings.language) overrides whatever language was chosen on
  // the login/reset-password screen. This is by design — the account setting is
  // authoritative for authenticated sessions. When localizationSettings.language
  // is null/undefined the pre-login choice from localStorage is preserved.
  // Theme has no server-side equivalent and is never overridden here.
  const userLanguage = user?.localizationSettings?.language;
  useEffect(() => {
    if (userLanguage && i18n.language !== userLanguage) {
      i18n.changeLanguage(userLanguage);
    }
  }, [userLanguage]);

  /**
   * NavGroup tanımları; labelKey'ler i18n'den çözümlenir.
   * useMemo: t() referansı değiştiğinde (dil değişikliği) yeniden üretilir.
   */
  const navGroups: NavGroup[] = useMemo(
    () => [
      {
        labelKey: "nav.group.general",
        items: [
          {
            labelKey: "nav.dashboard",
            path: "/dashboard",
            icon: (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
            ),
          },
        ],
      },
      {
        labelKey: "nav.group.catalog",
        items: [
          ...(canViewProducts
            ? [
                {
                  labelKey: "nav.products",
                  path: "/dashboard/products",
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <path d="M16 10a4 4 0 0 1-8 0" />
                    </svg>
                  ),
                },
              ]
            : []),
          ...(canViewCategories
            ? [
                {
                  labelKey: "nav.categories",
                  path: "/dashboard/categories",
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  ),
                },
              ]
            : []),
          ...(canViewBrands
            ? [
                {
                  labelKey: "nav.brands",
                  path: "/dashboard/brands",
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M8 12h8M12 8v8" />
                    </svg>
                  ),
                },
              ]
            : []),
          ...(canViewAttributes
            ? [
                {
                  labelKey: "nav.attributes",
                  path: "/dashboard/attributes",
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ),
                },
              ]
            : []),
          {
            labelKey: "nav.stockMovements",
            path: "/dashboard/stock-movements",
            icon: (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M9 17H5a2 2 0 0 0-2 2v0a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v0a2 2 0 0 0-2-2h-4" />
                <polyline points="12 3 12 15" />
                <polyline points="8 11 12 15 16 11" />
              </svg>
            ),
          },
          {
            labelKey: "nav.media",
            path: "/dashboard/media",
            icon: (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="9" cy="9" r="2" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            ),
          },
          ...(canImportProducts || canImportCurrentAccounts
            ? [
                {
                  labelKey: "nav.imports",
                  path: "/dashboard/imports",
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  ),
                },
              ]
            : []),
        ],
      },
      {
        labelKey: "nav.group.stock",
        items: [
          ...(canViewWarehouses
            ? [
                {
                  labelKey: "nav.warehouses",
                  path: "/dashboard/warehouses",
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                      <polyline points="9 22 9 12 15 12 15 22" />
                    </svg>
                  ),
                },
              ]
            : []),
          ...(canViewStockReceipts
            ? [
                {
                  labelKey: "nav.stockReceipts",
                  path: "/dashboard/stock-receipts",
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <rect x="5" y="2" width="14" height="20" rx="2" />
                      <line x1="9" y1="7" x2="15" y2="7" />
                      <line x1="9" y1="11" x2="15" y2="11" />
                      <line x1="9" y1="15" x2="12" y2="15" />
                    </svg>
                  ),
                },
              ]
            : []),
        ],
      },
      // Satış: siparişler günlük iş, teklifler onun öncesindeki adım. Sıralama bu
      // sıklığı yansıtıyor. Eşleşmeyen ürünler, gelen faturalar ve fatura serileri
      // burada ayrı satır DEĞİL: ilki Siparişler sayfasındaki uyarı şeridinden,
      // diğer ikisi Faturalar bölümünün sekmelerinden açılıyor.
      {
        labelKey: "nav.group.sales",
        items: [
          ...(canViewOrders
            ? [
                {
                  labelKey: "nav.orders",
                  path: "/dashboard/orders",
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                      <rect x="9" y="3" width="6" height="4" rx="1" />
                      <line x1="9" y1="12" x2="15" y2="12" />
                      <line x1="9" y1="16" x2="12" y2="16" />
                    </svg>
                  ),
                },
              ]
            : []),
          ...(canViewQuotes
            ? [
                {
                  labelKey: "nav.quotes",
                  path: "/dashboard/quotes",
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                    </svg>
                  ),
                },
              ]
            : []),
          ...(canViewInvoices
            ? [
                {
                  labelKey: "nav.invoices",
                  path: INVOICE_ROUTES.root,
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                      <polyline points="10 9 9 9 8 9" />
                    </svg>
                  ),
                },
              ]
            : []),
          ...(canViewDeliveryNotes
            ? [
                {
                  labelKey: "nav.deliveryNotes",
                  path: "/dashboard/delivery-notes",
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <rect x="1" y="3" width="15" height="13" />
                      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                      <circle cx="5.5" cy="18.5" r="2.5" />
                      <circle cx="18.5" cy="18.5" r="2.5" />
                    </svg>
                  ),
                },
              ]
            : []),
        ],
      },
      {
        labelKey: "nav.group.finance",
        items: [
          ...(canViewReports
            ? [
                {
                  labelKey: "nav.reports",
                  path: "/dashboard/reports",
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M3 3v18h18" />
                      <rect x="7" y="11" width="3" height="6" />
                      <rect x="12" y="7" width="3" height="10" />
                      <rect x="17" y="13" width="3" height="4" />
                    </svg>
                  ),
                },
              ]
            : []),
          ...(canViewCurrentAccounts
            ? [
                {
                  labelKey: "nav.currentManagement",
                  path: "/dashboard/accounts",
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <line x1="15" y1="7" x2="23" y2="7" />
                      <line x1="15" y1="11" x2="21" y2="11" />
                    </svg>
                  ),
                },
              ]
            : []),
          ...(canViewCashBank
            ? [
                {
                  labelKey: "nav.cashBank",
                  path: "/dashboard/cash-bank",
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <rect x="2" y="7" width="20" height="14" rx="2" />
                      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                      <line x1="12" y1="12" x2="12" y2="16" />
                      <circle cx="12" cy="12" r="2" />
                    </svg>
                  ),
                },
              ]
            : []),
          {
            labelKey: "nav.expenses",
            path: "/dashboard/expenses",
            icon: (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M20 12V8H6a2 2 0 0 1 0-4h12v4" />
                <path d="M4 6v12a2 2 0 0 0 2 2h14v-4" />
                <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
              </svg>
            ),
          },
          {
            labelKey: "nav.priceLists",
            path: "/dashboard/price-lists",
            icon: (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            ),
          },
          ...(canViewCheckNotes
            ? [
                {
                  labelKey: "nav.checkNotes",
                  path: "/dashboard/check-notes",
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <rect x="3" y="4" width="18" height="16" rx="2" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                      <line x1="9" y1="4" x2="9" y2="20" />
                    </svg>
                  ),
                },
              ]
            : []),
        ],
      },
      // Operasyon — Marketplace Sync: aktif marketplace modülü VE Marketplace.Read izni varsa göster
      ...(user?.activeModules?.some((m) =>
        (MARKETPLACE_PROVIDERS as readonly string[]).includes(m),
      ) && canViewMarketplace
        ? [
            {
              labelKey: "nav.group.operations",
              items: [
                {
                  labelKey: "nav.marketplaceSync",
                  path: "/dashboard/marketplace-sync",
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1-6.7-3M3 12a9 9 0 0 1 9-9 9 9 0 0 1 6.7 3" />
                      <polyline points="17 8 21 3 21 8" />
                      <polyline points="7 16 3 21 3 16" />
                    </svg>
                  ),
                },
              ],
            },
          ]
        : []),
      // Settings — TenantAdmin implicit-all; Member yalnız 1+ settings permission'i varsa goruyor
      ...(user?.role === "TenantAdmin" ||
      (user?.permissions ?? []).some((p) => p.startsWith("Settings."))
        ? [
            {
              labelKey: "nav.group.settings",
              items: [
                {
                  labelKey: "nav.settings",
                  path: SETTINGS_ROUTES.root,
                  icon: <Settings width="16" height="16" aria-hidden="true" />,
                },
              ],
            },
          ]
        : []),
    ],
    [
      user?.activeModules,
      user?.role,
      user?.permissions,
      canViewOrders,
      canViewInvoices,
      canViewQuotes,
      canViewDeliveryNotes,
      canViewProducts,
      canViewCategories,
      canViewBrands,
      canViewAttributes,
      canViewWarehouses,
      canViewStockReceipts,
      canViewReports,
      canViewCurrentAccounts,
      canViewCashBank,
      canViewCheckNotes,
      canViewMarketplace,
      canImportProducts,
      canImportCurrentAccounts,
    ],
  );

  // Active link: prefix-match; exact-match for /dashboard root.
  const isActive = useCallback(
    (path: string) =>
      path === "/dashboard"
        ? pathname === "/dashboard"
        : pathname.startsWith(path),
    [pathname],
  );

  // Aktif route'un parent grubunu otomatik aç.
  const expandGroup = useSidebarGroupsStore((s) => s.expandGroup);
  useEffect(() => {
    const active = navGroups.find((g) => g.items.some((i) => isActive(i.path)));
    if (active) expandGroup(active.labelKey);
  }, [pathname, navGroups, expandGroup, isActive]);

  // S7: logout revoke'u server'da basarisiz olursa sessizce yutmak yerine kullaniciya goster.
  // navigateRaw (not the view-transition `navigate`) on purpose — logout clears auth state
  // mid-transition, and this call predates useLogout, which preserves that choice unchanged.
  const { handleLogout, loggingOut, logoutError } = useLogout(navigateRaw);

  const profileDropdownItems = useMemo(
    () => [
      {
        labelKey: "sidebar.profile.dropdown.profile",
        icon: <User size={14} aria-hidden="true" />,
        onClick: () => navigate(PROFILE_ROUTE),
      },
      {
        labelKey: "sidebar.profile.dropdown.password",
        icon: <KeyRound size={14} aria-hidden="true" />,
        onClick: () => navigate(PROFILE_ROUTE),
      },
    ],
    [navigate],
  );

  return (
    <AppShell
      navGroups={navGroups}
      badge={{
        text: "v2",
        className:
          "text-success bg-success/10 border-success/20",
      }}
      isActive={isActive}
      profileDropdownItems={profileDropdownItems}
      showNotifications={true}
      showCurrencySwitcher={true}
      tenantId={tenantId}
      handleLogout={handleLogout}
      loggingOut={loggingOut}
      logoutError={logoutError}
      onNavigate={navigate}
      homePath={DASHBOARD_ROUTE}
    >
      <Outlet />
    </AppShell>
  );
};

export default TenantLayout;
